import React, { useEffect, useMemo, useState } from "react";
import { AppBar, Avatar, Box, IconButton, Toolbar, Tooltip, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';

import HeaderLogo from "../../../../../assets/svg/Varian Wordmark 1.svg";
import AlertsIcon from "../../../../../assets/svg-icon-for-menu/alerts-icon.svg";
import { getRole, getSwitchCompany } from "../../../../../helper/helper";
import { getApiUrl } from "../../../../../helper/config";
import { buildCompanyPath } from "../../../../../helper/url-builder";
import RightArrowIcon from "../../../../../assets/icons/right-arrow-icon";
import useUser from "../../../../../custom-hook/use-user";
import FeedbackDialog from "../../../dialog-component/feedback-dialog";

const drawerWidth = 250;
const companyLogoBlobCache = new Map();

function HeaderBox({ switchToMainUser, ...props }) {
    const { getActiveUser, getCompanyId } = useUser();
    const navigate = useNavigate();
    const urlParams = useParams();
    const authUser = useSelector((state) => state.authUser);
    const ActiveUser = getActiveUser();
    const [logoSrc, setLogoSrc] = useState(null);

    const companyId = urlParams.companyId || getCompanyId()?.toString() || '';
    const parentCompanyName = getSwitchCompany(ActiveUser);
    const managedBy = ActiveUser?.managedBy;
    const parentUser = authUser.users.length > 1 ? authUser.users[1] : null;

    const logoRequestUrl = useMemo(() => {
        if (!parentCompanyName) return null;

        const logoCompanyId = managedBy?.companyId || parentUser?.companyId;
        let logoPath = managedBy?.logoUrl || '';
        if (
            !logoPath &&
            parentUser?.logoUrl &&
            String(parentUser.companyId) === String(logoCompanyId)
        ) {
            logoPath = parentUser.logoUrl;
        }
        if (!logoPath && !managedBy?.companyId && parentUser?.logoUrl) {
            logoPath = parentUser.logoUrl;
        }

        if (logoPath) {
            if (/^https?:\/\//i.test(logoPath)) return logoPath;
            const base = getApiUrl().replace(/\/$/, "");
            return `${base}${logoPath.startsWith("/") ? logoPath : `/${logoPath}`}`;
        }
        if (logoCompanyId) {
            return `${getApiUrl()}/admin/api/v1/company/${logoCompanyId}/logo`;
        }
        return null;
    }, [parentCompanyName, managedBy?.companyId, managedBy?.logoUrl, parentUser]);

    useEffect(() => {
        if (!logoRequestUrl || !authUser.token) {
            setLogoSrc(null);
            return undefined;
        }

        const cached = companyLogoBlobCache.get(logoRequestUrl);
        if (cached) {
            setLogoSrc(cached);
            return undefined;
        }

        let cancelled = false;
        axios
            .get(logoRequestUrl, { responseType: 'blob', headers: { Authorization: authUser.token } })
            .then((response) => {
                if (cancelled) return;
                const objectUrl = URL.createObjectURL(response.data);
                companyLogoBlobCache.set(logoRequestUrl, objectUrl);
                setLogoSrc(objectUrl);
            })
            .catch(() => {
                if (!cancelled) setLogoSrc(null);
            });

        return () => {
            cancelled = true;
        };
    }, [logoRequestUrl, authUser.token]);

    const [feedbackOpen, setFeedbackOpen] = useState(false);

    function onClickAlert() {
        navigate(buildCompanyPath.alerts(companyId));
    }

    return (
        <>
            <AppBar position="fixed"
                sx={{ background: "#fff", height: "55px", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <Toolbar disableGutters={true}
                    sx={{ paddingLeft: "20px !important", minHeight: "55px !important" }}>

                    <Box sx={{ display: "flex", width: "100%" }}>
                        <Box
                            sx={{
                                flex: `${drawerWidth}px 0`,
                                display: "flex",
                                alignItems: "center",
                                paddingLeft: { xs: "8px", s: "12px", md: "16px" },
                            }}
                        >
                            <Box
                                component="img"
                                src={HeaderLogo}
                                alt="Logo"
                                sx={{
                                    height: { xs: 22, sm: 30, md: 40 },
                                    maxHeight: { xs: 22, sm: 30, md: 40 },
                                }}
                            />
                        </Box>


                        <Box sx={{ flex: "1 1 fit-content", display: "flex", gap: "10px", alignItems: "center" }}>
                            {!!parentCompanyName && !!logoSrc && (
                                <Avatar src={logoSrc} />
                            )}
                            <Typography
                                variant={"h6"}
                                sx={{
                                    background: "#fff",
                                    color: (theme) => theme.palette.text.primary,
                                    fontSize: { xs: "0.7rem", sm: "1.0rem", md: "1.25rem" },
                                    letterSpacing: { xs: "0.02em", sm: "0.03", md: "normal" }
                                }}
                            >
                                {parentCompanyName}
                            </Typography>

                            <IconButton onClick={switchToMainUser}>
                                <RightArrowIcon />
                            </IconButton>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flex: "fit-content 0 fit-content",
                                height: "100%",
                                paddingX: "10px",
                                gap: "10px",
                                alignItems: "center"
                            }}
                        >
                            <Box sx={{ flex: "fit-content" }}>
                                <Tooltip title="Send Feedback">
                                    <IconButton
                                        onClick={() => setFeedbackOpen(true)}
                                        sx={{ p: 0.5 }}
                                    >
                                        <FeedbackOutlinedIcon sx={{ width: "24px", height: "24px", color: "#666" }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Box sx={{ flex: "fit-content" }}>
                                <Avatar
                                    src={AlertsIcon}
                                    onClick={onClickAlert}
                                    sx={{ width: "30px", height: "30px", cursor: "pointer" }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    flex: "fit-content",
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    gap: "5px"
                                }}
                            >
                                <Avatar src={authUser?.profileImage} key={authUser?.userUpdateCounter} />
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                    <Typography
                                        color={"text.disabled"} variant={"subtitle1"}
                                        sx={{
                                            fontSize: { xs: "0.7rem", sm: "0.8rem", md: "1.0rem" },
                                        }}
                                    >
                                        {authUser?.firstName} {authUser?.lastName}
                                    </Typography>

                                    <Typography
                                        color={"text.disabled"} variant={"subtitle2"}
                                        sx={{
                                            fontSize: { xs: "0.6rem", sm: "0.8rem", md: "0.9rem" },
                                        }}
                                    >
                                        {getRole(authUser?.role).roleName}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Toolbar>
            </AppBar>
            <FeedbackDialog
                open={feedbackOpen}
                onClose={() => setFeedbackOpen(false)}
            />
        </>
    );
}

export default HeaderBox;
